import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { getProgram, getProgramCurriculums } from '../service/content';
import { getUserProgress } from '../service/user';
import {
  makeSelectAuthToken,
  makeSelectProgressChanged,
  makeSelectUserId,
  makeSelectUserProgram,
} from '../store/selectors';
import { notify } from '../utils/helper';

export const AppContext = React.createContext();

const AppProvider = ({ children }) => {
  const userId = useSelector(makeSelectUserId());
  const token = useSelector(makeSelectAuthToken());
  const userProgram = useSelector(makeSelectUserProgram());
  const progressChanged = useSelector(makeSelectProgressChanged());
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [program, setProgram] = useState(null);
  const [coreCurriculum, setCoreCurriculum] = useState(null);
  const [endorsements, setEndorsements] = useState([]);
  const [progress, setProgress] = useState([]);
  const [query, setQuery] = useState('');

  const handleContinueCourse = (id) => () => {
    const curriculum = [coreCurriculum, ...endorsements].find(
      ({ id: cid }) => cid === id
    );

    ReactGA.event({
      category: 'Content',
      action: 'Clicked curriculum',
      label: curriculum.title || id,
    });

    history.push(`/programs/${program.id}/${id}/courses`);
  };

  const fetchProgram = async () => {
    if (userProgram) {
      setLoading(true);
      try {
        const res = await getProgram(userProgram);
        if (res.data && res.data.status) {
          if (res.data.data) {
            setProgram(res.data.data);

            const curRes = await getProgramCurriculums(res.data.data.id);

            setEndorsements(curRes.data.data.filter(({ isCore }) => !isCore));
            const coreCur = curRes.data.data.filter(({ isCore }) => isCore);
            if (coreCur[0]) {
              setCoreCurriculum(coreCur[0]);
            }
          }

          setLoading(false);
          return;
        }

        notify('success', `${res.data.message}`);
      } catch (err) {
        notify('error', `An error occurred: ${err && err.toString()}`);
      }

      setLoading(false);
    }
  };

  const fetchUserProgress = async () => {
    setLoadingProgress(true);
    try {
      const res = await getUserProgress(userId);
      const { user } = res.data.data;

      setProgress(user.progress || []);
    } catch (err) {
      console.log('[err.fetch.user.progress]', err);
      notify(
        'error',
        'Error in fetching user progress: ' + (err && err.toString())
      );
    }
    setLoadingProgress(false);
  };

  useEffect(() => {
    if (userProgram) {
      fetchProgram();
    }
  }, [userProgram, userId, token]);

  useEffect(() => {
    if (userId) {
      fetchUserProgress();
    }
  }, [progressChanged, userId]);

  return (
    <AppContext.Provider
      value={{
        loading: loading || loadingProgress,
        program,
        coreCurriculum,
        progress,
        endorsements,
        query,
        setQuery,
        setProgress,
        fetchProgram,
        onContinueCourse: handleContinueCourse,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
